import { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { useSnackbar } from '../contexts/SnackbarProvider';
import { Controller } from 'react-hook-form';
import InputFieldAddress from '../components/InputFieldAddress';
import DefinitionRow from '../components/DefinitionRow';
import {
    Typography, Button, Stack, IconButton, Paper, Alert,
    Card, CardContent, TextField, MenuItem, Chip
} from '@mui/material';
import { Apartment, Person, PersonAdd, Delete } from '@mui/icons-material';
import { getFields, clientFields, contactFields } from '../utils/fields';

const ClientInfoForm = ({
    clientData,
    refetch,
    isEditMode,
    setIsEditMode,
    register,
    errors,
    setValue,
    control,
    watch,
    reset
}) => {
    const { showSnackbar } = useSnackbar();
    const fieldsToInclude = [
        'client_name', 'branch_name', 'postal_code',
        'client_phone', 'client_fax', 'industry_id',
        'business_description', 'website_url',
        'payment_method_id', 'payment_terms', 'credit_terms'
    ];

    const clientItems = getFields(clientFields, fieldsToInclude);

    //  フラット構造
    const items = watch('items') ?? [];

    const [industryOptions, setIndustryOptions] = useState([]);

    // --- マスタ取得 ---
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [indRes, payRes] = await Promise.all([
                    axiosInstance.get('/list/industries', { withCredentials: false }),
                ]);
                setIndustryOptions(indRes.data || []);
            } catch (err) {
                if (process.env.NODE_ENV === 'development') console.error(err);
            }
        };
        fetchOptions();
    }, []);

    // --- 初期データ ---
    useEffect(() => {
        if (!clientData) return;

        reset({
            ...clientData,
            items: clientData.items ?? []
        });

    }, [clientData, reset]);

    // --- 追加 ---
    const handleAddContact = () => {
        const current = watch('items') || [];

        setValue('items', [
            ...current,
            {
                client_contact_name: '',
                client_contact_email: '',
                client_contact_phone: ''
            }
        ]);
    };

    // --- 削除 ---
    const handleInvalidateContact = async (contact, index) => {
        const current = watch('items');

        if (!contact.client_contact_id) {
            setValue(
                'items',
                current.filter((_, i) => i !== index)
            );
            return;
        }

        if (current.length <= 1) {
            showSnackbar('担当者は1人以上登録してください。', 'warn');
            return;
        }

        const isConfirmed = window.confirm('本当に削除しますか？');
        if (!isConfirmed) return;

        try {
            await axiosInstance.put(
                `/client/contact/${contact.client_contact_id}/invalidate`
            );
            showSnackbar('削除しました', 'success');
            refetch();
            setIsEditMode(false);

        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
            showSnackbar(err?.response?.data?.message || '削除に失敗しました', 'error');
        }
    };

    return (
        <>
            {/* 会社情報 */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 4 }}>
                <Apartment color="primary" />
                <Typography variant="h3">会社情報</Typography>
            </Stack>

            <Paper variant="section">
                <Stack>
                    {clientItems.map(({ label, name, type, placeholder, validation, inputProps }) => {

                        // --- 業種 ---
                        if (name === 'industry_id') {
                            return (
                                <DefinitionRow key={name} label={label}>
                                    <Controller
                                        name={name}
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                select
                                                {...field}
                                                disabled={!isEditMode}
                                                error={!!errors?.[name]}
                                                helperText={errors?.[name]?.message}
                                                SelectProps={{ displayEmpty: true }}
                                            >
                                                <MenuItem value="">選択してください</MenuItem>
                                                {industryOptions.map((item) => (
                                                    <MenuItem key={item.industry_id} value={item.industry_id}>
                                                        {item.industry_name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                </DefinitionRow>
                            );
                        }

                        // --- 住所 ---
                        if (name === 'postal_code') {
                            return (
                                <DefinitionRow key={name} label="営業所所在地">
                                    <InputFieldAddress
                                        register={register}
                                        errors={errors}
                                        isEditMode={isEditMode}
                                        setValue={setValue}
                                        control={control}
                                    />
                                </DefinitionRow>
                            );
                        }

                        // --- 通常 ---
                        return (
                            <DefinitionRow key={name} label={label}>
                                <TextField
                                    type={type}
                                    placeholder={placeholder}
                                    disabled={!isEditMode}
                                    {...register(name, validation)}
                                    error={!!errors?.[name]}
                                    helperText={errors?.[name]?.message}
                                    inputProps={inputProps}
                                    multiline
                                />
                            </DefinitionRow>
                        );
                    })}
                </Stack>
            </Paper>

            {/* 担当者 */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 4 }}>
                <Person color="primary" />
                <Typography variant="h3">担当者情報</Typography>
            </Stack>
            <Paper variant="section">
                {items.length < 1 &&
                    <Alert severity="error" sx={{ mb: 2 }}>
                        担当者は1人以上登録してください。
                    </Alert>
                }
                {items.map((form, index) => (
                    <Card key={form.client_contact_id ?? `new-${index}`}>
                        <CardContent sx={{ p: 0 }}>

                            <Stack direction="row" justifyContent="space-between">
                                <Chip label={`登録${index + 1}`} color="primary" />
                                {!isEditMode && items.length > 1 && (
                                    <IconButton
                                        color="error"
                                        onClick={() => handleInvalidateContact(form, index)}
                                    >
                                        <Delete />
                                    </IconButton>
                                )}
                            </Stack>

                            <Stack>
                                {contactFields.map(({ label, name, type, placeholder, validation, inputProps }) => {

                                    const fieldName = `items.${index}.${name}`;
                                    const fieldError = errors?.items?.[index]?.[name];

                                    return (
                                        <DefinitionRow key={`${name}-${index}`} label={label}>
                                            <TextField
                                                type={type}
                                                placeholder={placeholder}
                                                disabled={!isEditMode}
                                                {...register(fieldName, validation)}
                                                error={!!fieldError}
                                                helperText={fieldError?.message}
                                                inputProps={inputProps}
                                                multiline
                                            />
                                        </DefinitionRow>
                                    );
                                })}
                            </Stack>

                        </CardContent>
                    </Card>
                ))}

                {isEditMode && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PersonAdd />}
                        onClick={handleAddContact}
                    >
                        担当者を追加
                    </Button>
                )}
            </Paper>
        </>
    );
};

export default ClientInfoForm;